import { useRef } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import Box from "../../../Common/Box/Box";
import BoxControls from "../../../Common/Box/BoxControls";
import Chart from "../../../Common/Box/Chart";
import { chartDownloadImageScaleFactor } from "../../../../constants";
import exportCharts from "../../../../export";
import IndicatorChooser from "../../IndicatorChooser";
import {
  getIndicatorDetails,
  getMethodPlural,
  getQuantitiesAndCostsMethodPlural,
  getTooltip,
  toTitleCase,
} from "../../../../utilities";

const key = "FP_CP_f5";
const number = 5;

const getMaximumValue = (data) => {
  let maximumValue = 0;

  for (let method of data) {
    for (let year of method.years) {
      maximumValue = Math.max(maximumValue, year.value);
    }
  }

  return maximumValue;
};

const getSeriesColor = (name, theme) => {
  switch (name) {
    case "Low":
      return theme.palette.gni.lic;

    case "Lower-Middle":
      return theme.palette.gni.lmic;

    case "Upper-Middle":
      return theme.palette.gni.umic;

    default:
      return "black";
  }
};

const options = (data, maximumValue, showYAxis, indicatorDetails) => (
  theme
) => {
  const color = getSeriesColor(data.name, theme);

  const series = [
    {
      name: data.name,
      color,
      marker: {
        lineColor: color,
      },
      data: data.years.map((year) => [year.year, year.value]),
    },
  ];

  return {
    chart: {
      type: "spline",
      height: 350,
      backgroundColor: null,
      width: showYAxis ? 236 : 180,
    },
    title: {
      text: data.name,
      align: "center",
      x: showYAxis ? 24 : undefined,
    },
    xAxis: {
      type: "datetime",
      min: 2017,
      max: 2032,
      tickPositions: [2019, 2025, 2030],
      tickLength: 0,
      labels: {
        formatter: function () {
          return [2019, 2030].includes(this.value) ? this.value : null;
        },
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    yAxis: {
      title: undefined,
      min: 0,
      max: maximumValue,
      labels: {
        enabled: showYAxis,
        style: {
          color: theme.figure.axis.secondary,
          fontSize: theme.figure.axis.fontSize,
        },
      },
    },
    plotOptions: {
      series: {
        lineWidth: 3,
        marker: {
          symbol: "circle",
          lineWidth: 3,
          fillColor: "white",
        },
        zoneAxis: "x",
        zones: [
          {
            value: 2025,
          },
          {
            dashStyle: "shortDot",
          },
        ],
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      backgroundColor: "white",
      useHTML: true,
      outside: true,
      formatter: function () {
        return getTooltip(indicatorDetails.name, this.y);
      },
      style: {
        fontSize: "0.875rem",
        color: theme.palette.tooltip.primary,
      },
    },
    exporting: {
      enabled: false,
    },
    series,
  };
};

const useStyles = makeStyles((theme) => ({
  content: {
    alignItems: "center",
    display: "flex",
  },
  facet: {
    margin: "2rem 0",
  },
  facetPanel: {
    display: "flex",
    justifyContent: "space-between",
  },
  legend: {
    marginLeft: theme.spacing(4),
  },
}));

const FpCpF5 = (props) => {
  const highchartRefs = [
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
    useRef(),
  ];

  const classes = useStyles();

  const indicatorDetails = getIndicatorDetails(props.indicatorId);

  const yAxisMaximum = getMaximumValue(props.data) * 1.02;

  return (
    <Box
      id={key}
      caption={`Figure ${number}`}
      title={`${indicatorDetails.qualifier} ${toTitleCase(
        props.indicatorId === 3 || props.indicatorId === 1
          ? getQuantitiesAndCostsMethodPlural(props.methodName)
          : getMethodPlural(props.methodName)
      )}`}
      subtitle="By Income Group, 2019-2030"
      controls={
        <BoxControls>
          <IndicatorChooser
            area="contraception"
            value={props.indicatorId}
            onChange={props.onIndicatorChange}
          />
        </BoxControls>
      }
      onDownloadPng={() => {
        exportCharts(
          {
            chart1: highchartRefs[0].current,
            chart2: highchartRefs[1].current,
            chart3: highchartRefs[2].current,
            chart4: highchartRefs[3].current,
            chart5: highchartRefs[4].current,
            chart6: highchartRefs[5].current,
            chart7: highchartRefs[6].current,
          },
          {
            filename: key,
            scale: chartDownloadImageScaleFactor,
          }
        );
      }}
    >
      <div className={classes.content}>
        <div className={classes.facetPanel}>
          {props.data.map((incomeGroup, index) => (
            <div key={incomeGroup.name} className={classes.facet}>
              <Chart
                highchartRef={highchartRefs[index]}
                options={options(
                  incomeGroup,
                  yAxisMaximum,
                  index === 0,
                  indicatorDetails
                )}
              />
            </div>
          ))}
        </div>
      </div>
    </Box>
  );
};

FpCpF5.propTypes = {
  methodName: PropTypes.string.isRequired,
  indicatorId: PropTypes.number.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      years: PropTypes.arrayOf(
        PropTypes.shape({
          year: PropTypes.number.isRequired,
          value: PropTypes.number.isRequired,
        })
      ),
    })
  ),
};

FpCpF5.defaultProps = {
  data: [],
};

export default FpCpF5;
